
/*
 * GABADINI SLEEP — CAMPAIGN CART CONTROLLER
 * Stage 2A: Read-only classification proof
 *
 * No cart changes or discount requests are made.
 */

(() => {
  'use strict';

  // Bundle handles confirmed in actual Shopify cart data.
  // Additional bundles will be added after verification.


  const verifiedBundleHandles = new Set([
    'fall-asleep-stack',
    'stay-asleep-stack',
    '3am-stack',
    'unwind-stack',
    'fall-asleep-stay-asleep-combo',
    'fall-asleep-3am-stack-combo'
  ]);

  const verifiedSingleHandles = new Set([
    'sleep-formula-melatonin',
    'magnesium-glycinate',
    'ashwagandha',
    'deep-calm-formula',
    '5-htp-nighttime-calm-support',
    'myo-d-chiro-inositol',
    'berberine',
    'saffron-extract',
    'neuro-plus-brain-and-focus',
    'menopause-nights'
  ]);


  function classifyItem(item) {
    if (!item.selling_plan_allocation) {
      return 'one-time';
    }

    if (verifiedBundleHandles.has(item.handle)) {
      return 'bundle-subscription';
    }

    if (verifiedSingleHandles.has(item.handle)) {
      return 'single-subscription';
    }

    return 'unclassified-subscription';
  }


  async function inspectCart() {
    const root = window.Shopify?.routes?.root || '/';

    const response = await fetch(root + 'cart.js', {
      credentials: 'same-origin'
    });

    if (!response.ok) {
      throw new Error(
        `Cart request failed: ${response.status}`
      );
    }

    const cart = await response.json();

    const lines = cart.items.map(item => ({
      product: item.product_title,
      handle: item.handle,
      classification: classifyItem(item),
      sellingPlan:
        item.selling_plan_allocation?.selling_plan?.name
        || 'None'
    }));


    const requiredCodes = [];

    if (lines.some(
      item => item.classification === 'bundle-subscription'
    )) {
      const code =
        window.GabadiniCampaigns?.FIRE50
          ?.discounts?.bundle?.code || 'SLEEP50';

      requiredCodes.push(code);
    }

    if (lines.some(
      item => item.classification === 'single-subscription'
    )) {
      const code =
        window.GabadiniCampaigns?.FIRE50
          ?.discounts?.single?.code || 'SLEEP50SINGLE';

      requiredCodes.push(code);
    }


    const existingCodes = (cart.discount_codes || [])
      .map(discount => discount.code);

    const result = {
      campaign: 'FIRE50',
      currency: cart.currency,
      lines,
      requiredCodes,
      existingCodes
    };

    console.table(lines);
    console.log('Gabadini campaign inspection:', result);

    return result;
  }

  
  /*
   * Stage 2B — Manually triggered bundle discount proof.
   * Does not run automatically.
   */

  async function applyBundleProof() {
    const result = await inspectCart();

    const bundleCode =
      window.GabadiniCampaigns?.FIRE50
        ?.discounts?.bundle?.code;

    if (!bundleCode) {
      throw new Error('FIRE50 bundle code not configured');
    }

    const hasBundle = result.lines.some(
      line => line.classification === 'bundle-subscription'
    );

    if (!hasBundle) {
      console.warn('No eligible bundle subscription found.');
      return { status: 'no-eligible-bundle' };
    }

    if (result.lines.some(
      line => line.classification === 'unclassified-subscription'
    )) {
      console.warn('Unknown subscription present. No changes made.');
      return { status: 'unclassified-products' };
    }

    const existing = result.existingCodes;

    // Avoid interfering with unrelated discounts.
    if (existing.some(
      code => code.toUpperCase() !== bundleCode.toUpperCase()
    )) {
      console.warn('Other discount codes present. No changes made.');
      return { status: 'other-discounts-present' };
    }

    if (existing.some(
      code => code.toUpperCase() === bundleCode.toUpperCase()
    )) {
      console.log('SLEEP50 is already applied. No update needed.');
      return { status: 'already-applied' };
    }

    const root = window.Shopify?.routes?.root || '/';

    const response = await fetch(root + 'cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        discount: bundleCode
      })
    });

    if (!response.ok) {
      throw new Error(
        `Discount update failed: ${response.status}`
      );
    }

    const cart = await response.json();

    const applied = (cart.discount_codes || []).some(
      code =>
        code.code?.toUpperCase() === bundleCode.toUpperCase()
        && code.applicable === true
    );

    console.log('Bundle discount proof:', {
      requestedCode: bundleCode,
      applied
    });

    return {
      status: applied ? 'applied' : 'not-applied',
      code: bundleCode
    };
  }


  /*
   * Stage 2D — Manual mixed-cart discount proof.
   * Runs only when called from the Console.
   */

  async function applyMixedProof() {
    const result = await inspectCart();
    const campaign = window.GabadiniCampaigns?.FIRE50;

    if (!campaign) {
      throw new Error('FIRE50 configuration not found');
    }

    const required = [
      campaign.discounts.bundle.code,
      campaign.discounts.single.code
    ];

    // Only run this proof with both product types present.
    if (!required.every(code => result.requiredCodes.includes(code))) {
      console.warn('Mixed subscription cart required.');
      return { status: 'not-mixed' };
    }

    // Never modify a cart containing unknown subscriptions.
    if (result.lines.some(
      line => line.classification === 'unclassified-subscription'
    )) {
      console.warn('Unknown subscription. No changes made.');
      return { status: 'unclassified-products' };
    }

    const normalize = code => String(code).toUpperCase();
    const requiredUpper = required.map(normalize);
    const existingUpper = result.existingCodes.map(normalize);

    // Protect unrelated customer discount codes.
    if (existingUpper.some(code => !requiredUpper.includes(code))) {
      console.warn('Other discounts present. No changes made.');
      return { status: 'other-discounts-present' };
    }

    if (requiredUpper.every(code => existingUpper.includes(code))) {
      console.log('Both campaign codes already present.');
      return { status: 'already-applied' };
    }

    const root = window.Shopify?.routes?.root || '/';

    const response = await fetch(root + 'cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        discount: required.join(',')
      })
    });

    if (!response.ok) {
      throw new Error(
        `Mixed discount update failed: ${response.status}`
      );
    }

    const cart = await response.json();

    const codes = required.map(code => ({
      code,
      applicable: (cart.discount_codes || []).some(
        discount =>
          normalize(discount.code) === normalize(code) &&
          discount.applicable === true
      )
    }));

    const items = cart.items.map(item => ({
      product: item.product_title,
      subscription: Boolean(item.selling_plan_allocation),
      discounts: (item.discounts || []).map(
        discount => discount.title
      )
    }));

    console.table(items);
    console.log('Mixed discount proof:', codes);

    return {
      status: codes.every(code => code.applicable)
        ? 'codes-applied'
        : 'needs-review',
      codes,
      items
    };
  }


  /*
   * Stage 2E — Manual discount reconciliation.
   * Applies, retains or removes FIRE50 campaign codes
   * according to the current cart contents.
   *
   * Does not run automatically.
   */

  async function reconcileProof() {
    const result = await inspectCart();
    const campaign = window.GabadiniCampaigns?.FIRE50;

    if (!campaign) {
      throw new Error('FIRE50 configuration not found');
    }

    const normalize = code =>
      String(code).trim().toUpperCase();

    const uniqueCodes = codes =>
      [...new Set(codes.map(normalize))].sort();

    const campaignCodes = uniqueCodes([
      campaign.discounts.bundle.code,
      campaign.discounts.single.code
    ]);

    // An expired FIRE50 campaign requires no campaign discounts.
    const identityStatus =
      window.GabadiniCampaignIdentity?.getStatus?.();

    const campaignExpired =
      identityStatus?.id === 'FIRE50' &&
      identityStatus.expired === true;

    const desired = campaignExpired
      ? []
      : uniqueCodes(result.requiredCodes);

    const existing = uniqueCodes(result.existingCodes);


    // Do not act if any subscription is unclassified.
    if (result.lines.some(
      line => line.classification ===
        'unclassified-subscription'
    )) {
      console.warn(
        'Unclassified subscription found. No cart changes.'
      );
      return { status: 'unclassified-products' };
    }

    // Protect unrelated customer discount codes.
    if (existing.some(
      code => !campaignCodes.includes(code)
    )) {
      console.warn(
        'Unrelated discount present. No cart changes.'
      );
      return { status: 'other-discounts-present' };
    }

    // Avoid unnecessary writes and update loops.
    const alreadyCorrect =
      desired.length === existing.length &&
      desired.every((code, index) =>
        code === existing[index]
      );

    if (alreadyCorrect) {
      console.log(
        'Campaign discounts already correct. No update.'
      );

      return {
        status: 'already-correct',
        codes: existing
      };
    }

    const root = window.Shopify?.routes?.root || '/';

    const response = await fetch(root + 'cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        discount: desired.join(',')
      })
    });

    if (!response.ok) {
      throw new Error(
        `Reconciliation failed: ${response.status}`
      );
    }

    const updatedCart = await response.json();

    const returnedCodes = updatedCart.discount_codes || [];

    const returned = uniqueCodes(
      returnedCodes.map(item => item.code)
    );

    const correctCodes =
      returned.length === desired.length &&
      desired.every((code, index) =>
        code === returned[index]
      );

    const allApplicable = desired.every(
      code => returnedCodes.some(
        item =>
          normalize(item.code) === code &&
          item.applicable === true
      )
    );

    const success = correctCodes && allApplicable;

    const outcome = {
      status: success ? 'reconciled' : 'needs-review',
      previous: existing,
      required: desired,
      returned,
      allApplicable
    };


    console.log('Campaign reconciliation:', outcome);

    // Refresh Dwell's cart display after a successful
    // Shopify campaign discount update.
    if (success) {
      try {
        const { DiscountUpdateEvent } =
          await import('@theme/events');

        document.dispatchEvent(
          new DiscountUpdateEvent(
            updatedCart,
            'gabadini-campaign-controller'
          )
        );

        console.log(
          'GABADINI: Native cart display refresh requested.'
        );
      } catch (error) {
        console.error(
          'GABADINI: Cart display refresh failed:',
          error
        );
      }
    }

    return outcome;
  }



  /*
   * Stage 2F — Automatic reconciliation test.
   *
   * OFF by default.
   * Enabled manually in the test browser session.
   * Uses Dwell's observed cart:update event.
   */

  const AUTO_TEST_KEY = 'gabadini-fire50-auto-test';

  let autoRunning = false;
  let autoPending = false;

  function autoTestEnabled() {
    try {
      return sessionStorage.getItem(AUTO_TEST_KEY) === 'on';
    } catch (error) {
      return false;
    }
  }

  function enableAutoTest() {
    sessionStorage.setItem(AUTO_TEST_KEY, 'on');
    console.log('GABADINI: FIRE50 automatic test enabled.');
    return { enabled: true };
  }

  function disableAutoTest() {
    sessionStorage.removeItem(AUTO_TEST_KEY);
    console.log('GABADINI: FIRE50 automatic test disabled.');
    return { enabled: false };
  }


  function campaignModeActive() {
    const identity = window.GabadiniCampaignIdentity;
    const status = identity?.getStatus?.();

    return (
      autoTestEnabled() ||
      identity?.getActive?.() === 'FIRE50' ||
      (
        status?.id === 'FIRE50' &&
        status.expired === true
      )
    );
  }


  async function runAutomaticReconciliation() {
    if (!campaignModeActive()) return;


    // Prevent overlapping discount requests.
    if (autoRunning) {
      autoPending = true;
      return;
    }

    autoRunning = true;

    try {
      do {
        autoPending = false;

        if (!campaignModeActive()) break;

        const result = await reconcileProof();

        console.log(
          'GABADINI AUTOMATIC RECONCILIATION:',
          result
        );

      } while (autoPending && campaignModeActive());

    } catch (error) {
      console.error(
        'GABADINI AUTOMATIC RECONCILIATION ERROR:',
        error
      );
    } finally {
      autoRunning = false;
    }
  }


  // Reconcile after Dwell cart updates, but only
  // when campaign identity or manual test mode is active.
  document.addEventListener(
    'cart:update',
    () => {
      if (!campaignModeActive()) return;

      void runAutomaticReconciliation();
    },
    true
  );

  // Remove campaign discounts when eligibility expires
  // while the storefront remains open.
  document.addEventListener(
    'gabadini:campaign-expired',
    () => {
      void runAutomaticReconciliation();
    }
  );


  // Also check an existing cart when a campaign visitor
  // opens or returns to a storefront page.
  if (campaignModeActive()) {
    void runAutomaticReconciliation();
  }


 
  // Read-only product classification for campaign PDP pricing.
  // Uses the same verified product lists as cart reconciliation.
  function classifyProductHandle(handle) {
    if (verifiedBundleHandles.has(handle)) {
      return 'bundle';
    }

    if (verifiedSingleHandles.has(handle)) {
      return 'single';
    }

    return null;
  }

  window.GabadiniCampaignController = {
    inspectCart,
    applyBundleProof,
    applyMixedProof,
    reconcileProof,
    enableAutoTest,
    disableAutoTest,
    autoTestEnabled,
    classifyProductHandle
  };
})();





